import { useState } from "react";
import { Upload } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import useToast from "~/components/ui/toast";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { type PublishedAppSummary, projectAppsPath, publishAppPath } from "~/services/apps";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";

interface PublishAppButtonProps {
  projectId: string;
  conversationId: string;
  // The agent the published app calls its tools as.
  agentId: string;
  appName: string;
}

/** Publishes a session's draft app to its project, under the app's name. */
export const PublishAppButton = ({
  projectId,
  conversationId,
  agentId,
  appName,
}: PublishAppButtonProps) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => setOpen(true)}>
        <Upload className="size-4" />
        Publish
      </Button>
      {isOpen && (
        <PublishAppDialog
          projectId={projectId}
          conversationId={conversationId}
          agentId={agentId}
          appName={appName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const PublishAppDialog = ({
  projectId,
  conversationId,
  agentId,
  appName,
  onClose,
}: PublishAppButtonProps & { onClose: () => void }) => {
  const { showSuccess } = useToast();
  const [title, setTitle] = useState(() => defaultTitle(appName));
  const [isPublishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedTitle = title.trim();

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    try {
      await apiMutate<PublishedAppSummary>(publishAppPath(conversationId, appName), "POST", {
        title: trimmedTitle,
        agent_id: agentId,
      });
      await mutate(projectAppsPath(projectId));
      showSuccess(`Published “${trimmedTitle}” to the project`);
      onClose();
    } catch (e) {
      setError(isApiError(e) ? getApiErrorMessage(e) : "Failed to publish the app.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Publish app"
      description="Project members open it from the project's Apps list, and its state there is shared by the project. Publishing again replaces it; its state carries over."
      onPrimary={handlePublish}
      primaryLabel="Publish"
      primaryPendingLabel="Publishing…"
      primaryDisabled={!trimmedTitle}
      isPending={isPublishing}
    >
      <div className="flex flex-col gap-2 py-2">
        <Label htmlFor="app-title">Title</Label>
        <Input
          id="app-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoComplete="off"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </FormDialog>
  );
};

function defaultTitle(appName: string): string {
  const words = appName.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
